const healthStyles = `
  :root { color-scheme: light; background: #ffffff; color: #1f2937; }
  * { box-sizing: border-box; }
  body { display: grid; min-height: 100vh; margin: 0; place-items: center; overflow: hidden;
    padding: 1rem; background: #ffffff;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
  .health-card { width: min(38rem, 100%); padding: clamp(1.75rem, 5vw, 3rem);
    border: 1px solid #e5e7eb; border-radius: 1rem; background: #ffffff;
    box-shadow: 0 .75rem 2.5rem rgb(15 23 42 / 10%); }
  .health-status { display: inline-flex; align-items: center; gap: .6rem; padding: .45rem .8rem;
    border: 1px solid #bfdbfe; border-radius: 999px; color: #1d4ed8;
    background: #eff6ff; font-size: .78rem; font-weight: 700; letter-spacing: .08em;
    text-transform: uppercase; }
  .health-status[data-health-status="healthy"] { border-color: #bbf7d0; color: #166534;
    background: #f0fdf4; }
  .health-status[data-health-status="healthy"] .health-dot { box-shadow: 0 0 0 .25rem rgb(22 101 52 / 10%); }
  .health-status[data-health-status="unhealthy"] { border-color: #fecaca; color: #991b1b;
    background: #fef2f2; }
  .health-status[data-health-status="unhealthy"] .health-dot { box-shadow: 0 0 0 .25rem rgb(153 27 27 / 10%); }
  .health-dot { width: .55rem; height: .55rem; border-radius: 50%; background: currentcolor;
    box-shadow: 0 0 0 .25rem rgb(37 99 235 / 10%); }
  .health-card h1 { margin: 1.5rem 0 .75rem; color: #111827; font-size: clamp(1.8rem, 7vw, 2.6rem);
    line-height: 1.15; letter-spacing: -.035em; }
  .health-card p { margin: 0; color: #4b5563; font-size: 1rem; line-height: 1.65; }
  .health-version { margin-top: 1.5rem !important; padding-top: 1.25rem; border-top: 1px solid #e5e7eb;
    color: #6b7280 !important; font: .82rem/1.5 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important; }
`;

function getIndexResources(): string[] {
  const resources = new Set<string>();

  document.querySelectorAll<HTMLScriptElement>("script[src]").forEach((element) => {
    resources.add(element.src);
  });
  document.querySelectorAll<HTMLLinkElement>("link[href]").forEach((element) => {
    resources.add(element.href);
  });

  return [...resources].filter((url) => new URL(url).origin === window.location.origin);
}

async function checkResource(url: string): Promise<boolean> {
  try {
    const head = await fetch(url, { method: "HEAD", cache: "no-store" });
    if (head.ok || head.status === 304) return true;

    // Some servers may not support HEAD for static assets.
    if (head.status === 405 || head.status === 501) {
      const get = await fetch(url, { cache: "no-store" });
      return get.ok || get.status === 304;
    }

    return false;
  } catch {
    return false;
  }
}

export function renderHealthPage() {
  document.title = "Health Status";
  document.documentElement.lang = "en";
  document
    .querySelector<HTMLMetaElement>('meta[name="viewport"]')
    ?.setAttribute("content", "width=device-width, initial-scale=1");

  const style = document.createElement("style");
  style.textContent = healthStyles;
  document.head.append(style);

  const app = document.querySelector<HTMLDivElement>("#app");
  if (app === null) {
    throw new Error("Health check cannot find the application mount point");
  }

  app.innerHTML = `
    <main class="health-card">
      <div class="health-status" data-health-status="checking" role="status">
        <span class="health-dot" aria-hidden="true"></span>
        <span data-health-label>Checking availability</span>
      </div>
      <h1 data-health-heading>Availability check in progress</h1>
      <p data-health-message>Please wait while the service status is being verified.</p>
      <p class="health-version">Version ${document.querySelector<HTMLMetaElement>('meta[name="application-version"]')?.content ?? "unknown"}</p>
    </main>`;

  const resources = getIndexResources();
  Promise.all(resources.map(checkResource)).then((results) => {
    const isHealthy = results.every(Boolean);

    app
      .querySelector<HTMLElement>("[data-health-status]")
      ?.setAttribute("data-health-status", isHealthy ? "healthy" : "unhealthy");
    const label = app.querySelector<HTMLElement>("[data-health-label]");
    const heading = app.querySelector<HTMLElement>("[data-health-heading]");
    const message = app.querySelector<HTMLElement>("[data-health-message]");

    if (label) label.textContent = isHealthy ? "Available" : "Unavailable";
    if (heading) {
      heading.textContent = isHealthy ? "Service is available" : "Service is unavailable";
    }
    if (message) {
      message.textContent = isHealthy
        ? "The service is operating normally."
        : "The service is currently unable to complete the availability check.";
    }
  });
}
